import { z } from 'zod'

import { classApi, classPath, classRoute } from '../class-form/class'

/** Where a class's roster lives: beside Class Details, reached from its side menu. */
export const classRosterRoute = `${classRoute}/roster`

/** A class's Class Roster page. */
export function classRosterPath(id: string): string {
  return `${classPath(id)}/roster`
}

/** Reading a class's roster. */
export const readClassRosterPermission = 'classes.class-roster.read'

/** The API's roster of a class. */
export function classRosterApi(id: string): string {
  return `${classApi(id)}/roster`
}

/** Entitlements' enrollment statuses, in legacy's order; only Enrolled holds a seat. */
export const enrollmentStatuses = ['requested', 'waitlisted', 'enrolled', 'denied', 'canceled'] as const
export type EnrollmentStatus = (typeof enrollmentStatuses)[number]

/** How a student attends: the location type. */
export const attendanceModes = ['inPerson', 'virtual', 'toBeDecided'] as const
export type AttendanceMode = (typeof attendanceModes)[number]

/** An Enrolled student's result in the class, in legacy's order; `notRecorded` is legacy's Unknown. */
export const completionStatuses = ['notRecorded', 'attending', 'complete', 'incomplete', 'noShow', 'failed'] as const
export type CompletionStatus = (typeof completionStatuses)[number]

const studentSchema = z.object({
  enrollmentId: z.string(),
  userProfileId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  status: z.enum(enrollmentStatuses),
  classroomId: z.string().nullish(),
  classroomName: z.string().nullish(),
  attendance: z.enum(attendanceModes),
  retake: z.boolean(),
  completionStatus: z.enum(completionStatuses),
  grade: z.number().int().nullish(),
  hoursSpent: z.number().int().nullish(),
})

/** One enrollment on a roster. */
export type RosterStudent = z.infer<typeof studentSchema>

const rosterSchema = z.object({
  classId: z.string(),
  className: z.string(),
  startsOn: z.string(),
  minimumStudents: z.number().int(),
  maximumStudents: z.number().int(),
  mayRecordResults: z.boolean(),
  settableCompletionStatuses: z.array(z.enum(completionStatuses)),
  students: z.array(studentSchema),
})

/** A class's roster: every enrollment, whatever its status, by last and first name. */
export type ClassRoster = z.infer<typeof rosterSchema>

export function parseClassRoster(body: unknown): ClassRoster {
  return rosterSchema.parse(body)
}

/** The roster's status filter: legacy's "All but canceled" first, then all, then each status. */
export const rosterFilters = ['allButCanceled', 'all', ...enrollmentStatuses] as const
export type RosterFilter = (typeof rosterFilters)[number]

/** "Last, First" as the mockup lists students, else the display name, else the id when Site Management has no profile. */
export function studentName(student: Pick<RosterStudent, 'firstName' | 'lastName' | 'displayName' | 'userProfileId'>): string {
  if (student.lastName !== '' && student.firstName !== '') {
    return `${student.lastName}, ${student.firstName}`
  }
  const name = student.lastName !== '' ? student.lastName : student.firstName !== '' ? student.firstName : student.displayName
  return name !== '' ? name : student.userProfileId
}

/**
 * The student's display name, as Entitlements names them in the roster's Room and Enrollment
 * controls, so every control in a row names the student the same way (WCAG 3.2.4); else the listed name.
 */
export function studentControlName(student: Pick<RosterStudent, 'firstName' | 'lastName' | 'displayName' | 'userProfileId'>): string {
  return student.displayName !== '' ? student.displayName : studentName(student)
}

/** The roster's completion filter: all, then each status. */
export const completionFilters = ['all', ...completionStatuses] as const
export type CompletionFilter = (typeof completionFilters)[number]

export function matchesCompletion(student: RosterStudent, filter: CompletionFilter): boolean {
  return filter === 'all' || (student.status === 'enrolled' && student.completionStatus === filter)
}

/** Whether the class's first session has started: the roster shows results from then, as legacy does. */
export function hasStarted(roster: ClassRoster, now: number = Date.now()): boolean {
  return Date.parse(roster.startsOn) <= now
}

/** The statuses offered for a result: those the caller may set, and its current one, which always stays. */
export function completionOptions(settable: readonly CompletionStatus[], current: CompletionStatus): CompletionStatus[] {
  return completionStatuses.filter((status) => status === current || settable.includes(status))
}

/** The most completion statuses one roster save takes: the API refuses more in one all-or-none save. */
export const maxRosterChanges = 500

/** Sets completion statuses on the roster, all or none. */
export function classRosterCompletionStatusesApi(id: string): string {
  return `${classRosterApi(id)}/completion-statuses`
}

/** Where one enrollment's result panel lives: under the roster it is reached from. */
export const enrollmentResultRoute = `${classRosterRoute}/:enrollmentId`

/** One enrollment's result page. */
export function enrollmentResultPath(classId: string, enrollmentId: string): string {
  return `${classRosterPath(classId)}/${enrollmentId}`
}

/** The API's result of one enrollment in its class. */
export function enrollmentResultApi(classId: string, enrollmentId: string): string {
  return `${classRosterApi(classId)}/${enrollmentId}`
}

export function matchesFilter(student: RosterStudent, filter: RosterFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'allButCanceled':
      return student.status !== 'canceled'
    default:
      return student.status === filter
  }
}
